/**
 * Contract / assessment-type values stored on patients as
 * `customFields.assessmentType`.
 *
 * Why this module exists:
 * - The same string list was previously copy-pasted across patient setup,
 *   the clinician dashboard filter, Provider Analytics, and org-signup
 *   templates. Those copies drifted (especially for The Owl Centre), which
 *   made typed analytics buckets look empty even when patients had valid
 *   contracts.
 * - Keeping one FE source of truth means adding a value like "NHS - Kent"
 *   is a single edit here, then wiring imports.
 *
 * Backend mirror: `ASSESSMENT_TYPES_BY_ORG` in
 * `python-functions/functions/analytics/provider_analytics.py` must stay in
 * sync for demographics/outcomes bucketing. MDT Org intentionally uses
 * Well Life Clinic's setup config for create/edit but is NOT listed here,
 * so it does not get a dashboard/analytics contract filter.
 *
 * TOC report validation uses a separate Python config
 * (`toc_report/contract_config.py`) — do not fold WLC strings into that file.
 */

export const ASSESSMENT_TYPES_BY_ORG = {
  'Well Life Clinic': [
    'Private',
    'NHS - SABP',
    'NHS - RTC',
    'NHS - Kent',
  ],
  // Order and values must match owlCentreConfig / ClinicianDashboard and the
  // keys used by the TOC report contract config.
  'The Owl Centre': [
    'National RTC',
    'Kent & Medway Integrated Care Board',
    'Cambridge & Peterborough Integrated Health Board',
    'Telford Hospital NHS Trust',
    'Private',
    'HR100',
    'Lexxic Ltd',
    'Futureworks University',
    'Airbus',
  ],
  // Demo subset used for sandbox demos (not the full Owl list).
  'Demo Org': [
    'Private',
    'National RTC',
    'Lexxic Ltd',
  ],
};

/**
 * Whether an organisation categorises patients by contract type.
 *
 * This is the single gate for "should contract-type UI appear for this org?".
 * It mirrors how Provider Analytics decides whether to show the contract
 * filter (an org only qualifies if it has at least one configured contract),
 * so the patient contract-type badge, the dashboard filter and analytics all
 * stay in lockstep instead of each maintaining their own hardcoded org list.
 *
 * Orgs such as SLaM, Phoenix, Lancaster and MDT are intentionally absent from
 * `ASSESSMENT_TYPES_BY_ORG`, so they return false here and never surface
 * contract-type UI even if a stray value exists on a patient record.
 */
export function orgHasContractTypes(organisationName) {
  return (ASSESSMENT_TYPES_BY_ORG[organisationName] || []).length > 0;
}

/**
 * Whether the clinician dashboard tables show a dedicated Contract column
 * (contract pill plus subcontract pill when present).
 *
 * This is TOC-only: The Owl Centre and its Demo Org sandbox. Well Life
 * Clinic categorises patients by contract type (so the in-patient badge and
 * dashboard filter still appear) but does not get this extra table column.
 * Uses substring matching so named branches ("The Owl Centre - Bath") still
 * qualify.
 */
export function orgShowsDashboardContractColumn(organisationName) {
  if (typeof organisationName !== 'string' || !organisationName) return false;
  return (
    organisationName.includes('The Owl Centre')
    || organisationName.includes('Demo Org')
  );
}

/**
 * react-select / dropdown options for a given organisation.
 * Returns [] when the org has no contract filter (hides the UI).
 */
export function assessmentTypeSelectOptions(organisationName) {
  return (ASSESSMENT_TYPES_BY_ORG[organisationName] || []).map((value) => ({
    value,
    label: value,
  }));
}

/**
 * Setup-config option objects ({ value, label }) for Well Life Clinic's
 * Contract Type step. MDT Org reuses the WLC deferred config, so it inherits these.
 */
export function wellLifeContractTypeOptions() {
  return assessmentTypeSelectOptions('Well Life Clinic');
}

/**
 * Contract type options for Well Life Clinic's CYP ADHD pathway.
 *
 * CYP referrals at WLC are private-only for now — there are no NHS-funded
 * CYP ADHD contracts (SABP, RTC, Kent are adult ADHD only). Kept as a
 * separate function rather than inlining the value so the CYP setup config
 * and any future CYP-specific dashboard filter pull from one place.
 */
export function wellLifeCypContractTypeOptions() {
  return [{ value: 'Private', label: 'Private' }];
}

/**
 * Setup-config option objects for The Owl Centre / Demo Org / Lancaster
 * autism-pathway contract dropdowns that share the Owl list.
 */
export function owlCentreContractTypeOptions() {
  return assessmentTypeSelectOptions('The Owl Centre');
}

// ─── Subcontract types ──────────────────────────────────────────────────────
// Some contracts require a subcontract field. Stored on the patient document
// as customFields.subContractType.
//
// Who collects it:
// - TOC Adult Autism: National RTC (this ICB list) and Hampshire & Isle of
//   Wight (a separate two-value sub-pathway list).
// - TOC Adult ADHD and TOC CYP ADHD: National RTC only, using this same ICB
//   list. Hampshire's adult-autism sub-pathways are not offered on ADHD.
//   Lancaster Adult ADHD does not collect a subcontract (see
//   ADULT_ADHD_NATIONAL_RTC_SUBCONTRACT_ORGS).
// - Other Adult ADHD orgs do not collect a subcontract.
//
// National RTC subcontracts: the commissioning Integrated Care Board (ICB).
// Names are the official ODS ALL-CAPS labels (NHS Organisation Data Service,
// PrimaryRoleId RO261 / Strategic Partnership, Status Active). "Other" was
// removed once the full ICB list replaced a short active-subcontract subset;
// "N/A" is kept for patients with no ICB to record. Alphabetical order so
// the long dropdown is scannable.
//
// Hampshire & Isle of Wight ICB subcontracts: two named sub-pathways
// distinguishing the geographic area for report-template gating.
//
// Backend mirror: SUBCONTRACT_TYPES / HAMPSHIRE_IOW_SUBCONTRACT_TYPES in
// python-functions/functions/toc_report/contract_config.py — keep both in
// sync.

export const SUBCONTRACT_TYPES = [
  'NHS BATH AND NORTH EAST SOMERSET, SWINDON AND WILTSHIRE INTEGRATED CARE BOARD',
  'NHS BEDFORDSHIRE, LUTON AND MILTON KEYNES INTEGRATED CARE BOARD',
  'NHS BIRMINGHAM AND SOLIHULL INTEGRATED CARE BOARD',
  'NHS BLACK COUNTRY INTEGRATED CARE BOARD',
  'NHS BRISTOL, NORTH SOMERSET AND SOUTH GLOUCESTERSHIRE INTEGRATED CARE BOARD',
  'NHS BUCKINGHAMSHIRE, OXFORDSHIRE AND BERKSHIRE WEST INTEGRATED CARE BOARD',
  'NHS CAMBRIDGESHIRE AND PETERBOROUGH INTEGRATED CARE BOARD',
  'NHS CENTRAL EAST INTEGRATED CARE BOARD',
  'NHS CHESHIRE AND MERSEYSIDE INTEGRATED CARE BOARD',
  'NHS CORNWALL AND THE ISLES OF SCILLY INTEGRATED CARE BOARD',
  'NHS COVENTRY AND WARWICKSHIRE INTEGRATED CARE BOARD',
  'NHS DERBY AND DERBYSHIRE INTEGRATED CARE BOARD',
  'NHS DEVON INTEGRATED CARE BOARD',
  'NHS DORSET INTEGRATED CARE BOARD',
  'NHS ESSEX INTEGRATED CARE BOARD',
  'NHS FRIMLEY INTEGRATED CARE BOARD',
  'NHS GLOUCESTERSHIRE INTEGRATED CARE BOARD',
  'NHS GREATER MANCHESTER INTEGRATED CARE BOARD',
  'NHS HAMPSHIRE AND ISLE OF WIGHT INTEGRATED CARE BOARD',
  'NHS HEREFORDSHIRE AND WORCESTERSHIRE INTEGRATED CARE BOARD',
  'NHS HERTFORDSHIRE AND WEST ESSEX INTEGRATED CARE BOARD',
  'NHS HUMBER AND NORTH YORKSHIRE INTEGRATED CARE BOARD',
  'NHS KENT AND MEDWAY INTEGRATED CARE BOARD',
  'NHS LANCASHIRE AND SOUTH CUMBRIA INTEGRATED CARE BOARD',
  'NHS LEICESTER, LEICESTERSHIRE AND RUTLAND INTEGRATED CARE BOARD',
  'NHS LINCOLNSHIRE INTEGRATED CARE BOARD',
  'NHS MID AND SOUTH ESSEX INTEGRATED CARE BOARD',
  'NHS NORFOLK AND SUFFOLK INTEGRATED CARE BOARD',
  'NHS NORFOLK AND WAVENEY INTEGRATED CARE BOARD',
  'NHS NORTH CENTRAL LONDON INTEGRATED CARE BOARD',
  'NHS NORTH EAST AND NORTH CUMBRIA INTEGRATED CARE BOARD',
  'NHS NORTH EAST LONDON INTEGRATED CARE BOARD',
  'NHS NORTH WEST LONDON INTEGRATED CARE BOARD',
  'NHS NORTHAMPTONSHIRE INTEGRATED CARE BOARD',
  'NHS NOTTINGHAM AND NOTTINGHAMSHIRE INTEGRATED CARE BOARD',
  'NHS SHROPSHIRE, TELFORD AND WREKIN INTEGRATED CARE BOARD',
  'NHS SOMERSET INTEGRATED CARE BOARD',
  'NHS SOUTH EAST LONDON INTEGRATED CARE BOARD',
  'NHS SOUTH WEST LONDON INTEGRATED CARE BOARD',
  'NHS SOUTH YORKSHIRE INTEGRATED CARE BOARD',
  'NHS STAFFORDSHIRE AND STOKE-ON-TRENT INTEGRATED CARE BOARD',
  'NHS SUFFOLK AND NORTH EAST ESSEX INTEGRATED CARE BOARD',
  'NHS SURREY AND SUSSEX INTEGRATED CARE BOARD',
  'NHS SURREY HEARTLANDS INTEGRATED CARE BOARD',
  'NHS SUSSEX INTEGRATED CARE BOARD',
  'NHS THAMES VALLEY INTEGRATED CARE BOARD',
  'NHS WEST AND NORTH LONDON INTEGRATED CARE BOARD',
  'NHS WEST YORKSHIRE INTEGRATED CARE BOARD',
  'N/A',
];

// Hampshire & Isle of Wight ICB subcontracts.
export const HAMPSHIRE_IOW_SUBCONTRACT_TYPES = [
  'Hampshire IOW Adult Autism',
  'Portsmouth Adult Autism',
];

/**
 * react-select / dropdown options for the National RTC subcontract type field.
 */
export function subContractTypeOptions() {
  return SUBCONTRACT_TYPES.map((value) => ({ value, label: value }));
}

/**
 * react-select / dropdown options for Hampshire IOW subcontract type field.
 */
export function hampshireIowSubContractTypeOptions() {
  return HAMPSHIRE_IOW_SUBCONTRACT_TYPES.map((value) => ({ value, label: value }));
}

/**
 * Mapping from parent contract value to the subcontract options that should
 * appear when that contract is selected. Used by the setup form to swap out
 * the subcontract dropdown options dynamically based on the selected contract.
 *
 * Contracts not in this map do not surface a subcontract field.
 */
export const SUBCONTRACT_OPTIONS_BY_CONTRACT = {
  'National RTC': subContractTypeOptions(),
  'Hampshire & Isle of Wight Integrated Care Board': hampshireIowSubContractTypeOptions(),
};

/**
 * Organisations whose Adult ADHD pathway asks for a National RTC subcontract.
 *
 * The Owl Centre records the commissioning ICB on Adult ADHD in the same
 * words as Adult Autism. Demo Org is the sandbox for that clinic, so it
 * asks the same question. Lancaster Medical Practice shares the Owl setup
 * shape but was left out of this change — its Adult ADHD pathway still
 * has no subcontract field.
 *
 * Backend mirror: ADULT_ADHD_NATIONAL_RTC_SUBCONTRACT_ORGS in
 * python-functions/functions/toc_report/contract_config.py.
 */
export const ADULT_ADHD_NATIONAL_RTC_SUBCONTRACT_ORGS = [
  'The Owl Centre',
  'Demo Org',
];

/**
 * Whether this organisation's Adult ADHD setup should ask for an ICB when
 * the contract is National RTC.
 *
 * Returns false for every other org, including Lancaster, so a missing or
 * unexpected name never starts showing the field.
 */
export function adultAdhdCollectsNationalRtcSubcontract(organisationName) {
  return ADULT_ADHD_NATIONAL_RTC_SUBCONTRACT_ORGS.includes(organisationName);
}
